import { useState, type ReactElement } from 'react'
import { Check, Truck, Clock, ShieldCheck } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Card } from '../components/ui/card.js'
import { Calendar } from '../components/ui/calendar.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore } from '../state/store.js'
import { activePersona } from '../lib/persona.js'
import { addDays, formatYMD } from '../lib/dates.js'
import { cn } from '@/lib/utils.js'

/** Convert YYYY-MM-DD string ↔ Date (UTC) for react-day-picker. */
const isoToDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d))
}

export const ScheduleScreen = (): ReactElement => {
  const { goto } = useStep()
  const { state, dispatch } = useDemoStore()
  const persona = activePersona()
  const today = new Date()
  const earliest = addDays(today, 3)        // earliest install window

  const initialISO = state.bookingWindow?.dateISO ?? persona.order.estimatedInstall
  const [selected, setSelected] = useState<Date | undefined>(isoToDate(initialISO))
  const [window, setWindow] = useState<'AM' | 'PM' | null>(state.bookingWindow?.window ?? 'AM')

  const dateISO = selected ? formatYMD(selected) : null

  const confirm = (): void => {
    if (!dateISO || !window) return
    dispatch({ type: 'SET_BOOKING_WINDOW', value: { dateISO, window } })
    goto('confirmation')
  }

  return (
    <AppShell
      title="Schedule"
      showBack onBack={() => goto('review')}
      progress={0.95}
      footer={
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Selected window</div>
            <div className="font-semibold truncate">{dateISO}, {window === 'AM' ? 'Morning' : 'Afternoon'}</div>
          </div>
          <Button onClick={confirm} className="shrink-0">Confirm Booking</Button>
        </div>
      }
    >
      <Card className="p-5 mb-4">
        <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-3">Service progress</div>
        <ul className="space-y-3">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Check className="h-3.5 w-3.5" />
            </span>
            <div><div className="font-semibold">Order placed</div><div className="text-xs text-muted-foreground">Paid</div></div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Check className="h-3.5 w-3.5" />
            </span>
            <div><div className="font-semibold">Dimensions & readiness</div><div className="text-xs text-muted-foreground">Confirmed via this assessment</div></div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
              <Truck className="h-3.5 w-3.5" />
            </span>
            <div><div className="font-semibold">Appliance delivery</div><div className="text-xs text-primary">Estimated 2 days before install</div></div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            </span>
            <div><div className="font-semibold">Installation</div><div className="text-xs text-muted-foreground italic">Pick your window below</div></div>
          </li>
        </ul>
      </Card>

      <Card className="p-3 mb-4 flex justify-center">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={setSelected}
          defaultMonth={selected ?? earliest}
          disabled={{ before: earliest }}
        />
      </Card>

      <Card className="p-4 mb-4">
        <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-2">Available timeslots</div>
        <div className="grid grid-cols-2 gap-2">
          {(['AM', 'PM'] as const).map(w => (
            <button
              key={w}
              type="button"
              onClick={() => setWindow(w)}
              className={cn(
                'rounded-xl py-3 font-semibold transition-colors inline-flex items-center justify-center gap-2',
                window === w
                  ? 'border-2 border-primary text-primary'
                  : 'border border-border text-foreground hover:border-primary/50',
              )}
            >
              <Clock className="h-4 w-4" />
              {w === 'AM' ? '8 AM – 12 PM' : '1 PM – 5 PM'}
            </button>
          ))}
        </div>
      </Card>

      <div className="bg-primary/5 border border-primary/15 rounded-2xl p-4 text-sm text-foreground">
        <div className="font-semibold mb-1 inline-flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Safety Net Protection
        </div>
        Don't worry about shipping delays. If your delivery is delayed, we'll <b>automatically reschedule</b> your pro for you at no extra cost.
      </div>
    </AppShell>
  )
}
